import classNames from "classnames";
import isEmpty from "lodash/isEmpty";
import { useActivityTemplateType } from "PFApp/activities/hooks";
import { TITLE_MAX_LENGTH } from "PFApp/booking/components/booking_form/booking_form_common_inputs.utils";
import { isGroupOption } from "PFApp/booking/components/booking_form/workforce_member_select/workforce_member_select.utils";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { TextArea } from "PFComponents/text/text_area";
import { getAttribute } from "PFCore/helpers/get_attribute";
import { useActivity } from "PFCore/hooks/queries/activity";
import { useBookingsFromAvailability } from "PFCore/hooks/queries/bookings/bookings_from_availability/use_bookings_from_availability";
import { IconName } from "PFTheme/graphics/icons";
import { BookingCategory } from "PFTypes";
import { Option } from "PFTypes/options";
import { useRef } from "react";
import { Controller, FieldPath, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { BookingDetailItem } from "../booking_detail_item";
import { BookingCategorySelect } from "./booking_category_select";
import css from "./booking_form.module.scss";
import { BookingFormValues } from "./booking_form_provider";
import { OverrideToggles } from "./override_toggles";

type BookingFormCommonInputsProps = {
  overridesDiaryTimeFormValueName: FieldPath<BookingFormValues>;
  overridesNonWorkingTimeFormValueName: FieldPath<BookingFormValues>;
  categoryFormValueName: FieldPath<BookingFormValues>;
  categoryFormErrorKeys: string[];
  titleFormValueName: FieldPath<BookingFormValues>;
  descriptionFormValueName: FieldPath<BookingFormValues>;
  isOverridesNonWorkingDaysDisabled: boolean;
  getCategoryTip?: (data: { options: Option<BookingCategory>[] }) => string | undefined;
};

export const BookingFormCommonInputs = ({
  overridesDiaryTimeFormValueName,
  overridesNonWorkingTimeFormValueName,
  categoryFormValueName,
  categoryFormErrorKeys,
  titleFormValueName,
  descriptionFormValueName,
  isOverridesNonWorkingDaysDisabled,
  getCategoryTip
}: BookingFormCommonInputsProps) => {
  const { t } = useTranslation("bookingModule");

  const { control, setValue } = useFormContext();

  const [activityId, workforceMember] = useWatch<BookingFormValues, ["activityId", "workforceMember"]>({
    name: ["activityId", "workforceMember"]
  });
  const category = useWatch<BookingFormValues>({ name: categoryFormValueName }) as BookingCategory | null;

  const { data: activitySelectedInForm } = useActivity(activityId ?? -1, {
    enabled: !!activityId,
    staleTime: Infinity
  });
  const { isRole: isBookingRoleRelated, isEngagement: isBookingEngagementRelated } =
    useActivityTemplateType(activitySelectedInForm);

  const { data: bookingsFromAvailabilityData } = useBookingsFromAvailability({
    activityId,
    profileId: isGroupOption(workforceMember) ? undefined : workforceMember?.id,
    options: {
      enabled: isBookingRoleRelated && !isGroupOption(workforceMember) && !!workforceMember?.id
    }
  });

  const inputTagRef = useRef<HTMLInputElement>(null);

  const handleFocus = () => {
    inputTagRef?.current?.select();
  };

  const getTitleActions = (title: string | undefined) => {
    let defaultTitleValue = "";
    if (!activityId || !!activitySelectedInForm?.private) {
      defaultTitleValue = getAttribute(category, "displayAs") ?? "";
    } else if (isBookingEngagementRelated) {
      defaultTitleValue = activitySelectedInForm?.name ?? "";
    } else if (isBookingRoleRelated) {
      defaultTitleValue = bookingsFromAvailabilityData?.title ?? "";
    }

    if (isEmpty(defaultTitleValue) || defaultTitleValue === title) {
      return [];
    }
    return [
      {
        name: "history" as IconName,
        onClick: () => setValue(titleFormValueName, defaultTitleValue)
      }
    ];
  };

  return (
    <>
      <OverrideToggles
        overridesCalendarFormValueName={overridesDiaryTimeFormValueName}
        overridesNonWorkingDaysFormValueName={overridesNonWorkingTimeFormValueName}
        isOverridesNonWorkingDaysDisabled={isOverridesNonWorkingDaysDisabled}
      />
      <BookingCategorySelect
        categoryFormValueName={categoryFormValueName}
        categoryFormErrorKeys={categoryFormErrorKeys}
        titleFormValueName={titleFormValueName}
        getTip={getCategoryTip}
      />
      <Controller
        name={titleFormValueName}
        control={control}
        render={({ field }) => (
          <InputFieldSet
            label={t("bookings.create.title")}
            value={field.value}
            maxLength={TITLE_MAX_LENGTH}
            onFocus={handleFocus}
            onChange={field.onChange}
            className={classNames(css.selectInput)}
            ref={inputTagRef}
            actions={getTitleActions(field.value)}
          />
        )}
      />
      <BookingDetailItem
        label={t("bookings.create.description")}
        value={
          <Controller
            name={descriptionFormValueName}
            control={control}
            render={({ field }) => (
              <TextArea
                title={t("bookings.create.description")}
                value={field.value}
                onChange={field.onChange}
                className={css.textarea}
                minHeight={145}
                maxLength={2000}
                placeholder={t("bookings.create.descriptionPlaceholder")}
              />
            )}
          />
        }
      />
    </>
  );
};
